<script setup lang="ts">
  defineOptions({ name: 'ProfileCardMenuPanel' })

  import type { PropType } from 'vue'
  import { computed } from 'vue'

  import {
    CreditCard01Icon,
    LogOut01Icon,
    Star05Icon,
    UserCircleIcon,
    UserSquareIcon,
    Users01Icon,
  } from '@gohighlevel/ghl-icons/24/outline'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPCardv2 from '@ui/components/CPCardv2.vue'
  import CPDivider from '@ui/components/CPDivider.vue'
  import CPText from '@ui/components/CPText.vue'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'
  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import { DEFAULT_IMAGES } from '../../core-layer/constants'

  import { PROFILE_MENU_ITEMS, PROFILE_MENU_OPTIONS, type ProfileMenuOption } from '../constants/profile-menu'

  import type { ProfileCardMenuHeaderVariant, ProfileCardMenuUserSlice, ProfileMenuIconName } from '../types/profile-menu'

  const props = defineProps({
    cardId: {
      type: String,
      required: true,
    },
    headerVariant: {
      type: String as PropType<ProfileCardMenuHeaderVariant>,
      required: true,
    },
    userProfile: {
      type: Object as PropType<ProfileCardMenuUserSlice | null>,
      default: null,
    },
    profileTitle: {
      type: String,
      required: true,
    },
  })

  const emit = defineEmits<{
    select: [option: ProfileMenuOption]
    close: []
  }>()

  const { t } = useI18n()

  const closePopover = () => emit('close')

  const IconComponents: Partial<Record<ProfileMenuIconName, typeof UserCircleIcon>> = {
    UserCircleIcon,
    UserSquareIcon,
    Star05Icon,
    Users01Icon,
    CreditCard01Icon,
    LogOut01Icon,
  }

  const { appType } = useGlobal()

  const visibleMenuItems = computed(() => PROFILE_MENU_ITEMS.filter(item => item.showOnApps.includes(appType)))

  const menuItemsStudio = computed(() => visibleMenuItems.value.filter(i => i.key === PROFILE_MENU_OPTIONS.KOLLAB_STUDIO))

  const menuItemsAccount = computed(() =>
    visibleMenuItems.value.filter(i =>
      (
        [PROFILE_MENU_OPTIONS.PROFILE_SETTINGS, PROFILE_MENU_OPTIONS.ACCOUNT_SETTINGS, PROFILE_MENU_OPTIONS.MANAGE_PAYMENTS] as string[]
      ).includes(i.key)
    )
  )

  const menuItemsAffiliates = computed(() => visibleMenuItems.value.filter(i => i.key === PROFILE_MENU_OPTIONS.AFFILIATES))

  function formatNameForAvatar(name: string) {
    if (!name) return 'U'
    return name.charAt(0).toUpperCase()
  }

  function onSelect(key: ProfileMenuOption) {
    emit('select', key)
  }
</script>

<template>
  <CPCardv2 :id="cardId" role="menu" class="!h-auto max-h-[min(90dvh,32rem)] w-full !p-0 !shadow-lg" :aria-label="t('profile.menu.title')">
    <!-- TopBar: centered header -->
    <template v-if="headerVariant === 'topbar-centered'">
      <div class="flex w-full flex-col items-center">
        <div class="flex w-full flex-col items-center">
          <div class="flex w-full flex-col items-center gap-2 p-4">
            <CPAvatar
              v-if="userProfile?.avatar"
              :id="createElementId('profile', 'menu', 'avatar')"
              size="xl"
              :src="userProfile.avatar"
              :alt="userProfile?.fullName ?? undefined"
              :title="userProfile?.fullName ?? undefined"
              :name="userProfile?.fullName ?? undefined"
            />
            <CPAvatar v-else size="xl">
              {{ formatNameForAvatar(userProfile?.fullName || t('profile.unnamed')) }}
            </CPAvatar>

            <div class="flex w-full flex-col items-center">
              <CPText :id="createElementId('profile', 'menu', 'name')" size="lg" weight="semibold" class="text-body w-full text-center">
                {{ userProfile?.fullName || t('profile.unnamed') }}
              </CPText>

              <CPText :id="createElementId('profile', 'menu', 'email')" size="sm" class="text-700 mb-2 w-full truncate text-center">
                {{ userProfile?.email || t('profile.emailPlaceholder') }}
              </CPText>
            </div>
          </div>
          <!-- Slot for an app-specific banner-style CTA above the menu items (e.g. GoKollab Pro upgrade). -->
          <slot name="extras-banner" :close="closePopover" />
          <CPDivider margin-top="0px" margin-bottom="0px" />
        </div>

        <div class="w-full">
          <div v-for="item in visibleMenuItems" :key="item.key">
            <div
              :id="createElementId('profile', 'menu', item.key)"
              role="menuitem"
              tabindex="0"
              class="text-700 flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-hover"
              @click="onSelect(item.key)"
              @keydown.enter="onSelect(item.key)"
              @keydown.space.prevent="onSelect(item.key)"
            >
              <component :is="IconComponents[item.icon]" class="h-4 w-4 flex-shrink-0" />
              <CPText size="md" weight="regular" class="truncate">
                {{ t(item.labelKey) }}
              </CPText>
            </div>
          </div>
          <!-- Slot for app-specific extra menu items rendered inline with the standard items (e.g. GoKollab Plans & Pricing). -->
          <slot name="extras-items" :close="closePopover" />
        </div>

        <CPDivider margin-top="0px" margin-bottom="0px" />

        <div class="w-full">
          <div
            :id="createElementId('profile', 'menu', 'logout-button')"
            role="menuitem"
            tabindex="0"
            class="text-danger flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-hover"
            @click="onSelect(PROFILE_MENU_OPTIONS.LOGOUT)"
            @keydown.enter="onSelect(PROFILE_MENU_OPTIONS.LOGOUT)"
            @keydown.space.prevent="onSelect(PROFILE_MENU_OPTIONS.LOGOUT)"
          >
            <LogOut01Icon class="h-4 w-4 flex-shrink-0" />
            <CPText size="md" weight="regular" class="truncate">
              {{ t('profileMenu.logOut') }}
            </CPText>
          </div>
        </div>
      </div>
    </template>

    <!-- Sidebar: compact row header + grouped actions -->
    <template v-else>
      <div class="flex w-full flex-col">
        <div class="flex w-full min-w-0 items-center gap-2 p-3">
          <CPAvatar
            :id="createElementId('profile', 'menu', 'avatar')"
            :src="userProfile?.avatar ?? undefined"
            :title="profileTitle"
            :img-props="{ alt: profileTitle }"
            shape="square"
            size="sm"
            class="flex-shrink-0"
          />
          <div class="grid min-w-0 flex-1 text-left">
            <CPText
              :id="createElementId('profile', 'menu', 'name')"
              size="lg"
              weight="medium"
              htmlTag="span"
              class="text-body truncate !leading-tight"
            >
              {{ userProfile?.fullName || t('profile.unnamed') }}
            </CPText>
            <CPText :id="createElementId('profile', 'menu', 'email')" size="sm" htmlTag="span" class="text-600 truncate !leading-tight">
              {{ userProfile?.email || t('profile.emailPlaceholder') }}
            </CPText>
          </div>
        </div>

        <!-- Slot for an app-specific banner-style CTA above the menu items
             (e.g. GoKollab Pro upgrade). Sidebar-row variant mirrors the
             topbar-centered placement so both layouts expose the same hooks. -->
        <slot name="extras-banner" :close="closePopover" />

        <CPDivider margin-top="0px" margin-bottom="0px" />

        <div class="w-full">
          <template v-if="menuItemsStudio.length">
            <div v-for="item in menuItemsStudio" :key="item.key">
              <div
                :id="createElementId('profile', 'menu', item.key)"
                role="menuitem"
                tabindex="0"
                class="text-700 flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-hover"
                @click="onSelect(item.key)"
                @keydown.enter="onSelect(item.key)"
                @keydown.space.prevent="onSelect(item.key)"
              >
                <component :is="IconComponents[item.icon]" class="h-4 w-4 flex-shrink-0" />
                <CPText size="md" weight="regular" class="truncate">
                  {{ t(item.labelKey) }}
                </CPText>
              </div>
            </div>
            <CPDivider margin-top="0px" margin-bottom="0px" />
          </template>

          <div v-for="item in menuItemsAccount" :key="item.key">
            <div
              :id="createElementId('profile', 'menu', item.key)"
              role="menuitem"
              tabindex="0"
              class="text-700 flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-hover"
              @click="onSelect(item.key)"
              @keydown.enter="onSelect(item.key)"
              @keydown.space.prevent="onSelect(item.key)"
            >
              <component :is="IconComponents[item.icon]" class="h-4 w-4 flex-shrink-0" />
              <CPText size="md" weight="regular" class="truncate">
                {{ t(item.labelKey) }}
              </CPText>
            </div>
          </div>

          <div v-for="item in menuItemsAffiliates" :key="item.key">
            <div
              :id="createElementId('profile', 'menu', item.key)"
              role="menuitem"
              tabindex="0"
              class="text-700 flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-hover"
              @click="onSelect(item.key)"
              @keydown.enter="onSelect(item.key)"
              @keydown.space.prevent="onSelect(item.key)"
            >
              <component :is="IconComponents[item.icon]" class="h-4 w-4 flex-shrink-0" />
              <CPText size="md" weight="regular" class="truncate">
                {{ t(item.labelKey) }}
              </CPText>
            </div>
          </div>

          <CPDivider margin-top="0px" margin-bottom="0px" />

          <div
            :id="createElementId('profile', 'menu', 'logout-button')"
            role="menuitem"
            tabindex="0"
            class="text-danger flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-hover"
            @click="onSelect(PROFILE_MENU_OPTIONS.LOGOUT)"
            @keydown.enter="onSelect(PROFILE_MENU_OPTIONS.LOGOUT)"
            @keydown.space.prevent="onSelect(PROFILE_MENU_OPTIONS.LOGOUT)"
          >
            <LogOut01Icon class="h-4 w-4 flex-shrink-0" />
            <CPText size="md" weight="regular" class="truncate">
              {{ t('profileMenu.logOut') }}
            </CPText>
          </div>
        </div>
      </div>
    </template>
  </CPCardv2>
</template>
